import type { AxisCounts, GridResolution, XyzDataPoint } from '../types';
import type { Surface3DViewSettings } from '../viewSettings';
import { maxOf, minOf } from './ranges';

/**
 * How many bins a `binned` chart divides each horizontal axis into, on an axis whose values are
 * not a short run of integers. An axis whose values are gets one bin per value whatever this is.
 *
 * `'auto'` is {@link FALLBACK_BIN_COUNT}.
 */
export type BinCount = 'auto' | number;

/** The most distinct integer values an axis can span and still get one bin per value. */
export const MAXIMUM_PER_VALUE_BINS = 12;

/** Bins along an axis that `'auto'` cannot give one per value. */
export const FALLBACK_BIN_COUNT = 16;

/** One bin per value on each axis where that applies, `null` where it does not. */
export type PerValueBinCounts = { x: number | null; y: number | null };

/**
 * The bin count that puts one bin on each integer from the lowest value to the highest.
 *
 * `null` unless every value is an integer and the run spans between 2 and
 * {@link MAXIMUM_PER_VALUE_BINS} integers.
 */
export const toPerValueBinCount = (values: number[]): number | null => {
  if (values.length === 0 || !values.every(Number.isInteger)) return null;

  const count = maxOf(values) - minOf(values) + 1;
  if (count < 2 || count > MAXIMUM_PER_VALUE_BINS) return null;
  return count;
};

/** {@link toPerValueBinCount} for each horizontal axis of the points. */
export const toPerValueBinCounts = (
  points: XyzDataPoint[]
): PerValueBinCounts => ({
  x: toPerValueBinCount(points.map(({ x }) => x)),
  y: toPerValueBinCount(points.map(({ y }) => y)),
});

/** Which horizontal axes get one bin per value. */
export type PerValueAxes = 'both' | 'x' | 'y' | 'none';

export const toPerValueAxes = ({ x, y }: PerValueBinCounts): PerValueAxes => {
  if (x !== null && y !== null) return 'both';
  if (x !== null) return 'x';
  if (y !== null) return 'y';
  return 'none';
};

const toSettingBinCount = (binCount: BinCount): number =>
  binCount === 'auto' ? FALLBACK_BIN_COUNT : binCount;

/**
 * The bins along each axis: one per value where the data allows it, the setting elsewhere.
 *
 * A stored count is not consulted on a per-value axis, and applies again once the data stops
 * qualifying.
 */
export const toBinCounts = (
  binCount: BinCount,
  perValue: PerValueBinCounts
): AxisCounts => ({
  x: perValue.x ?? toSettingBinCount(binCount),
  y: perValue.y ?? toSettingBinCount(binCount),
});

/** What a surface chart grids at: bins per axis under `binned`, the resolution setting otherwise. */
export const toGridResolution = (
  {
    interpolation,
    resolution,
  }: Pick<Surface3DViewSettings, 'interpolation' | 'resolution'>,
  binCounts: AxisCounts
): GridResolution => (interpolation === 'binned' ? binCounts : resolution);
